import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  AdminDeliveryMethod,
  DeliveryMethodSettingValue,
  IAdminSkuDeliverableRequest,
  SkuDeliverableInfo,
  Skus,
} from "services/KnowfullyAdminClient";
import { settingValuesToBind } from "./settingsToRender";
import SkuDeliveryMethodSetting, {
  SkuDeliveryMethodSettingProps,
} from "./SkuDeliveryMethodSetting";
import { DeliveryMethodHelper } from "utils/deliveryMethodHelper";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
};

type SkuDeliveryMethodFormProps = {
  onSubmit: (values: IAdminSkuDeliverableRequest) => void;
  onCancel?: () => void;
  sku: Skus;
  submitLabel: string;
  skuDeliveryMethod?: SkuDeliverableInfo;
  deliveryMethodSelected?: AdminDeliveryMethod | null;
};

type SkuDeliveryMethodFormValues = {
  skuId: string;
  id: string;
  deliveryMethodId: string;
  professionId: string;
  sequence: number;
  title?: string;
  settingValues?: DeliveryMethodSettingValue[];
};

const stringifySettingValues = (
  settingValues?: DeliveryMethodSettingValue[]
): DeliveryMethodSettingValue[] | undefined => {
  return settingValues?.map(
    (sv) =>
      new DeliveryMethodSettingValue({
        setting: sv.setting,
        value: sv.value?.toString(),
      })
  );
};

const { Form } = withTypes<SkuDeliveryMethodFormValues>();

const SkuDeliveryMethodCommonForm = ({
  sku,
  onCancel,
  onSubmit,
  submitLabel,
  skuDeliveryMethod,
  deliveryMethodSelected,
}: SkuDeliveryMethodFormProps &
  FormProps<IAdminSkuDeliverableRequest>) => {
  const { formatMessage: t } = useIntl();

  const init = useMemo<SkuDeliveryMethodFormValues>(
    () => ({
      skuId: sku?.id!,
      id: skuDeliveryMethod?.skuDeliverableId!,
      deliveryMethodId: skuDeliveryMethod?.deliveryMethodId! ?? "",
      professionId: "",
      sequence: skuDeliveryMethod?.sequence! ?? 1,
      settingValues: settingValuesToBind(skuDeliveryMethod?.settingValues),
      title: skuDeliveryMethod?.displayText ?? "",
    }),
    [sku, skuDeliveryMethod]
  );

  const deliveryMethodId =
    deliveryMethodSelected?.id ?? skuDeliveryMethod?.deliveryMethodId;

  const submitForm = async (values: SkuDeliveryMethodFormValues) => {
    const processedSettingValues = DeliveryMethodHelper.processSettingValues(
      deliveryMethodId,
      values.settingValues
    );

    return onSubmit({
      ...values,
      deliveryMethodId: deliveryMethodId,
      settingValues: stringifySettingValues(processedSettingValues),
    });
  };

  const isEdit = !!skuDeliveryMethod;

  return (
    <Form
      onSubmit={submitForm}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent dividers>
            <Grid container spacing={1}>
              <Field name="title" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      required
                      variant="standard"
                      fullWidth
                      label={t({ id: "courseActionsButtonText" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <SkuDeliveryMethodSetting
                deliveryMethodId={deliveryMethodId}
                settings={
                  (isEdit
                    ? values.settingValues?.map(
                        (x) =>
                          new SkuDeliveryMethodSettingProps(x.setting!, x.value)
                      )
                    : deliveryMethodSelected?.settings?.map(
                        (x) => new SkuDeliveryMethodSettingProps(x)
                      )) ?? []
                }
              />

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button type="submit" color="primary" disabled={submitting}>
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default SkuDeliveryMethodCommonForm;
